import Button from '@atlaskit/button';
import { Radio } from '@atlaskit/radio';
import SectionMessage from '@atlaskit/section-message';
import Select from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import { redirect } from 'redux-first-router';
import styled from 'styled-components';
import RedirectToProductList from './components/redirectToProductList';
import MACPageHeader from './components/macPageHeader';
import TrialLicenceItem from './components/TrialLicenceItem';
import { Dimensions } from '../../components/visuals';
import SidInstructions from './components/SidInstuctions';
import { State } from '../../model/State';
import { selectEvaluationProducts, selectProductPackages } from './duck/selectors/products';
import { isAllBTF } from '../../model/hams/Pricing';
import {
  ATLASSIAN_STACK,
  cloversKeys,
  sharePointKeys,
  sourceTreeKeys,
} from '../../data/products/keys';
import { messages } from './messages';
import { CloudProductEvaluationLinks } from '../../data/products/links';
import t from '../i18n/intl';
import { route } from './duck/actions/landing';
import { createPersistedLicenseAction } from './duck/actions/accountId';
import {
  getCurrentMACContactOrganization,
  getMACUserChannel,
  selectCreatedPersistedLicense,
} from './duck/selectors';
import * as preselectedLicense from './duck/actions/licensesPreselected';
import { addonLicenseView } from './duck/actions';
import {
  ContentBody,
  DetailLineWrapper,
  DetailsValueLabelWrapper,
  PageH3Header,
  SpinnerWrapper,
  StyledLoadingButton,
} from './components/styledComponents';
import { hostedAccountTypes } from '../../util/mac/helpers';

const ATLASSIAN_SERVER_KEYS = [
  'jira-servicedesk',
  'jira-core',
  'jira-software',
  'confluence',
  'stash',
  'crowd',
  'perforce.jira',
  'vss.jira',
];

const LicenseItems = styled.div`
  display: flex;
  grid-gap: ${Dimensions.units(1)}px;
`;

const SelectWrapper = styled.div`
  width: ${Dimensions.units(45)}px;
`;

const PageHeader = styled(PageH3Header)`
  padding-top: ${Dimensions.units(2)}px;
`;

const TextfieldWrapper = styled.div`
  width: ${Dimensions.units(37.5)}px;
`;

const InstanceOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailLine = ({ labelText, dataCy = '', children }) => {
  return (
    <DetailLineWrapper {...(dataCy ? { 'data-cy': dataCy } : {})}>
      <DetailsValueLabelWrapper>
        {labelText && <FormattedMessage {...labelText} />}
      </DetailsValueLabelWrapper>
      {children}
    </DetailLineWrapper>
  );
};
const LoadingState = () => (
  <SpinnerWrapper $marginTop={20} $height={20}>
    <Spinner size="large" />
  </SpinnerWrapper>
);

enum ErrorTypes {
  INVALID_SID = 'INVALID_SID',
  SERVER_ERROR = 'SERVER_ERROR',
  NONE = 'NONE',
}

const ErrorMap = {
  [ErrorTypes.INVALID_SID]: {
    title: t('mac.server-id-page.invalid.server.id.title'),
    message: messages.invalidServerIdMessage,
  },
  [ErrorTypes.SERVER_ERROR]: {
    title: t('mac.license.addon.evaluation.max.eval.title'),
    message: messages.errorServerDetails,
  },
};

const ErrorSection = ({ errorType, additionalValues }) => {
  if (!errorType || errorType === ErrorTypes.NONE) return null;
  const error = ErrorMap[errorType];

  return (
    <SectionMessage appearance="error" title={error.title}>
      <FormattedMessage {...error.message} values={{ ...error.values, ...additionalValues }} />
    </SectionMessage>
  );
};

const extractErrorString = (errorObject) => {
  if (errorObject?.error?.i18nFieldErrors) {
    return errorObject.error.i18nFieldErrors.map((obj) => obj?.defaultMessage).join(', ');
  }

  if (errorObject?.error?.fieldErrors) {
    return Object.entries(errorObject.error.fieldErrors)
      .map(([, value]) => `${value}`)
      .join(', ');
  }

  if (errorObject?.error?.errorDetail) {
    return errorObject.error.errorDetail;
  }
  return '';
};

const modifyJiraSoftwareProducts = (productList) => {
  return productList.map((product) => {
    const updatedProduct = { ...product };

    if (product.key === 'jira-software.ondemand') {
      updatedProduct.baseName = 'Jira';
      updatedProduct.description = 'Jira (Cloud)';
      updatedProduct.longDescription = 'Jira (Cloud)';
    } else if (product.key === 'jira-software.data-center') {
      updatedProduct.baseName = 'Jira';
      updatedProduct.description = 'Jira Software (Data Center)';
      updatedProduct.longDescription = 'Jira Software (Data Center)';
    }
    return updatedProduct;
  });
};

const removeDuplicates = (productsList) => {
  const uniqueRecordsMap = new Map<number, any>();
  productsList.forEach((record) => {
    uniqueRecordsMap.set(record.id, record);
  });
  return Array.from(uniqueRecordsMap.values());
};

const getProductListForEvaluation = (cloudProducts, evaluationProducts) => {
  if (!cloudProducts || !evaluationProducts) {
    return null;
  }

  const evaluationDetails = evaluationProducts
    ?.filter((p) => !ATLASSIAN_SERVER_KEYS.includes(p.details.key))
    .map((product) => {
      const { details } = product;
      const updatedDetails = { ...details };

      if (updatedDetails.baseName === 'Marketplace Addon') {
        updatedDetails.baseName = updatedDetails.description;
      }
      return updatedDetails;
    });

  const cloudProductsDetails = cloudProducts.map((p) => p.details);

  let concatProducts = modifyJiraSoftwareProducts(evaluationDetails?.concat(cloudProductsDetails));
  concatProducts = concatProducts
    .filter(
      (product) =>
        !['jira-core.ondemand', 'bamboo', 'pricingplan.bamboo'].includes(product.key.toLowerCase()),
    )
    .sort((a, b) => a.baseName.localeCompare(b.baseName));

  return removeDuplicates(concatProducts);
};

const isProductWithoutServerIDRequirement = (product: any) => {
  return (
    !isAllBTF(product.productType) ||
    ['ADDON', 'ADDON_PRICING_PLAN'].includes(product.productType) ||
    cloversKeys.includes(product.key) ||
    sharePointKeys.includes(product.key) ||
    sourceTreeKeys.includes(product.key) ||
    ATLASSIAN_STACK === product.key
  );
};

const getDefaultSelectedOptionFromUrl = (productListForEvaluation: any) => {
  const { searchParams } = new URL(window.location.href);
  const productKeyFromUrl = searchParams.get('productKey') || searchParams.get('product');

  if (productKeyFromUrl) {
    return productListForEvaluation?.find((product) =>
      product.key.toLowerCase().startsWith(productKeyFromUrl.toLowerCase()),
    );
  }
  return null;
};

const softwareLicenseAgreementLink = (chunks) => (
  <a
    href="https://www.atlassian.com/legal/software-license-agreement"
    target="_blank"
    rel="noreferrer"
    title="Software License Agreement"
  >
    {chunks}
  </a>
);

const privacyPolicy = (chunks) => (
  <a
    href="https://www.atlassian.com/legal/privacy-policy"
    target="_blank"
    rel="noreferrer"
    title="Privacy Policy"
  >
    {chunks}
  </a>
);

const LicenseEvaluationPage = () => {
  const preselectedSid = useSelector((state) => (state as State).location.query?.sid);
  const { isPartner } = useSelector((state: State) => getMACUserChannel(state));
  const cloudProducts = useSelector((state) => selectProductPackages(state as State));
  const evaluationProducts = useSelector((state) => selectEvaluationProducts(state as State));
  const contactOrganization = useSelector(getCurrentMACContactOrganization);
  const [selectedProductGroup, setSelectedProductGroup] = useState(null);
  const [baseNameFamilyProducts, setBaseNameFamilyProducts] = useState<any[]>();
  const [sidRequired, setSidRequired] = useState<boolean>(false);
  const [displayMainForm, setDisplayMainForm] = useState<boolean>(false);
  const [selectedProductKey, setSelectedProductKey] = useState<string | null>();
  const [organizationName, setOrganizationName] = useState(contactOrganization);
  const [serverId, setServerId] = useState<string>();
  const [errorType, setErrorType] = useState<ErrorTypes>(ErrorTypes.NONE);
  const [selectedInstanceOption, setSelectedInstanceOption] = useState('upAndRunning');
  const createLicenseResponse = useSelector(selectCreatedPersistedLicense);

  const dispatch = useDispatch();

  const showError = (errType: ErrorTypes) => {
    setErrorType(errType);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (baseNameFamilyProducts && baseNameFamilyProducts?.length === 1) {
      setSelectedProductKey(baseNameFamilyProducts[0].key);
      if (baseNameFamilyProducts[0].marketplaceAddOn === true) {
        // redirect to addon /tryaddon with productKey
      }
    }
  }, [baseNameFamilyProducts]);

  useEffect(() => {
    if (contactOrganization) {
      setOrganizationName(contactOrganization);
    }
  }, [contactOrganization]);

  useEffect(() => {
    if (!createLicenseResponse) {
      return;
    }
    if (createLicenseResponse?.success) {
      if (createLicenseResponse._result.productPackageDetails.atlassianAddOn === true) {
        dispatch(
          redirect(
            addonLicenseView.route(createLicenseResponse._result.licenseDetails.id.toString(), {}),
          ),
        );
      } else {
        const searchParams = new URLSearchParams(window.location.search);
        const product = searchParams.get('product') || searchParams.get('productKey');

        dispatch(
          redirect(
            preselectedLicense.licensePreselectedRoute(
              createLicenseResponse._result.licenseDetails.id.toString(),
              {
                product,
                callback: searchParams.get('callback'),
                licensefieldname: searchParams.get('licensefieldname'),
              },
            ),
          ), // need to redirect to newly created trial license, but trial licenses doesn't work now
        );
      }
    } else if (createLicenseResponse.error) {
      showError(ErrorTypes.SERVER_ERROR);
    }
  }, [createLicenseResponse]);

  useEffect(() => {
    if (selectedProductKey && baseNameFamilyProducts) {
      const selectedProduct = baseNameFamilyProducts.find((p) => p.key === selectedProductKey);
      const productKey = selectedProduct.key.replace('pricingplan.', '');
      if (Object.prototype.hasOwnProperty.call(CloudProductEvaluationLinks, productKey)) {
        const cloudTrialUrl = CloudProductEvaluationLinks[productKey];
        if (cloudTrialUrl) {
          window.location.href = cloudTrialUrl;
        }
      } else {
        setDisplayMainForm(true);
        if (!isProductWithoutServerIDRequirement(selectedProduct) && !isPartner) {
          setSidRequired(true);
        }
      }
    }
  }, [selectedProductKey]);

  const productListForEvaluation = useMemo(
    () => getProductListForEvaluation(cloudProducts, evaluationProducts),
    [cloudProducts, evaluationProducts],
  );
  const options = Array.from(new Set(productListForEvaluation?.map((p) => p.baseName))).map(
    (baseName) => {
      return { value: baseName, label: baseName };
    },
  );

  const handleSelectChange = (option) => {
    setSidRequired(false);
    setErrorType(ErrorTypes.NONE);
    setDisplayMainForm(false);
    setSelectedProductKey(null);
    setServerId('');
    setSelectedProductGroup(option);
    const filteredProducts = productListForEvaluation
      ?.filter(
        (product) => product.baseName.toLowerCase() === option.value.toLowerCase(), // should be sorted
      )
      .sort((a) => {
        if (hostedAccountTypes.includes(a.hostingType)) {
          return -1;
        }
        return 0;
      });

    setBaseNameFamilyProducts(filteredProducts);
  };

  useEffect(() => {
    if (productListForEvaluation) {
      const preselectedProductPackage = getDefaultSelectedOptionFromUrl(productListForEvaluation);
      if (preselectedProductPackage) {
        const { baseName, key } = preselectedProductPackage;
        handleSelectChange({ value: baseName, label: baseName });
        setSelectedProductKey(key);
        setServerId(preselectedSid);
      }
    }
  }, [productListForEvaluation]);

  const generateLicenseClick = (productKey: string) => {
    if (sidRequired && !serverId) {
      showError(ErrorTypes.INVALID_SID);
      return;
    }
    dispatch(
      createPersistedLicenseAction(
        productKey,
        organizationName || contactOrganization || '',
        serverId,
      ),
    );
  };

  return (
    <>
      <RedirectToProductList />
      <MACPageHeader />
      <ErrorSection
        errorType={errorType}
        additionalValues={{
          errorDetails: extractErrorString(createLicenseResponse),
        }}
      />
      <PageHeader>
        <FormattedMessage {...messages.licenceEvaluationPageTitle} />
      </PageHeader>
      <hr />
      {productListForEvaluation ? (
        <ContentBody $marginTop={2.5}>
          <DetailLine labelText={messages.productLabel}>
            <SelectWrapper data-cy="select-product-group">
              <Select
                options={options}
                onChange={handleSelectChange}
                value={selectedProductGroup}
                placeholder="-- Select Product --"
              />
            </SelectWrapper>
          </DetailLine>
          {baseNameFamilyProducts && (
            <DetailLine labelText={messages.licenseType}>
              <LicenseItems>
                {baseNameFamilyProducts?.map((product) => (
                  <TrialLicenceItem
                    key={product.key}
                    product={product}
                    setSelectedProductKey={setSelectedProductKey}
                    isSelected={selectedProductKey === product.key}
                    hideDescription={baseNameFamilyProducts.length === 1}
                  />
                ))}
              </LicenseItems>
            </DetailLine>
          )}
          {(sidRequired || displayMainForm) && (
            <DetailLine labelText={messages.organization}>
              <TextfieldWrapper>
                <Textfield
                  value={organizationName || ''}
                  isRequired
                  onChange={(e) => setOrganizationName(e.currentTarget.value)}
                  maxLength={100}
                />
              </TextfieldWrapper>
            </DetailLine>
          )}
          {selectedProductKey && sidRequired && (
            <>
              <DetailLine labelText={messages.instancelabel}>
                <InstanceOptionsWrapper>
                  <div>
                    <Radio
                      label={<FormattedMessage {...messages.upAndRunningInstance} />}
                      data-cy="instanceTypeRunning"
                      name="instance"
                      value="upAndRunning"
                      isChecked={selectedInstanceOption === 'upAndRunning'}
                      onChange={() => setSelectedInstanceOption('upAndRunning')}
                    />
                  </div>
                  <div>
                    <Radio
                      label={<FormattedMessage {...messages.notInstalledYetInstance} />}
                      data-cy="instanceTypeNotInstalled"
                      name="instance"
                      value="notInstalledYet"
                      isChecked={selectedInstanceOption === 'notInstalledYet'}
                      onChange={() => setSelectedInstanceOption('notInstalledYet')}
                    />
                  </div>
                </InstanceOptionsWrapper>
              </DetailLine>
              <DetailLine labelText={messages.serverIdLabel}>
                <TextfieldWrapper>
                  <Textfield
                    value={serverId || ''}
                    isRequired
                    name="sidInput"
                    id="sidInput"
                    data-cy="sidInput"
                    placeholder="ABCD-1234-EFGH-5678"
                    onChange={(e) => {
                      setErrorType(ErrorTypes.NONE);
                      setServerId(e.currentTarget.value);
                    }}
                  />
                </TextfieldWrapper>
              </DetailLine>

              <DetailLine labelText="">
                <SidInstructions
                  selectedInstanceOption={selectedInstanceOption}
                  productKey={selectedProductKey}
                />
              </DetailLine>
            </>
          )}
          {selectedProductKey && displayMainForm && (
            <DetailLine labelText="">
              <div>
                <p>
                  <FormattedMessage {...messages.licenseEvaluationSupportInfo} />
                </p>
                <p>
                  {t(messages.licenseEvaluationHostedServerTermsAgreement, {
                    softwareLicenseAgreementLink,
                    privacyPolicy,
                  })}
                </p>
              </div>
            </DetailLine>
          )}
          {(sidRequired || displayMainForm) && selectedProductKey && (
            <DetailLine labelText="">
              <StyledLoadingButton
                appearance="primary"
                onClick={() => generateLicenseClick(selectedProductKey)}
                isDisabled={errorType === ErrorTypes.INVALID_SID}
                data-cy="btn-generate-license"
                isLoading={createLicenseResponse?.inProgress}
              >
                <FormattedMessage {...messages.generateLicenseButtonMessage} />
              </StyledLoadingButton>
              <Button
                onClick={() => {
                  dispatch(redirect(route()));
                }}
                appearance="subtle"
              >
                <FormattedMessage {...messages.addNewContactModalCancel} />
              </Button>
            </DetailLine>
          )}
        </ContentBody>
      ) : (
        <LoadingState />
      )}
    </>
  );
};
export const MACLicenseEvaluationPage = connect(null, null)(LicenseEvaluationPage);
